// Punkt 5 aus docs/design/live-verlauf.md: die drei Live-Graphen.
//
// CPU (dazu jeder Kern einzeln), Arbeitsspeicher (Belegt / Cache / Auslagerung
// mit absoluten Werten) und der Durchsatz des wichtigsten Ports. Die Linien
// zeigen die letzten fünf Minuten; sie kommen aus derselben Verlaufs-Abfrage
// wie der große Graph, nur mit kurzem Fenster.
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatBitsPerSecond, formatBytes, formatNumber } from '../lib/format'
import type { Sample } from '../lib/types'
import { Sparkline, werte } from './Sparkline'

/** Fenster der Live-Graphen. */
export const LIVE_FENSTER = '5m'

export interface LiveKartenProps {
  deviceId: string
  /** Die Momentaufnahme aus /metrics, für Kerne und absolute Werte. */
  latest: Sample[]
  /** Abfrageintervall des Agents, solange live beobachtet wird. */
  intervalS: number
  /** Der Port, dessen Durchsatz gezeigt wird; leer heißt: keine Karte. */
  port: string
}

/** Ein Kern mit seiner Last. */
export interface Kern {
  index: string
  value: number
}

/**
 * Die einzelnen Kerne aus der Momentaufnahme. Im Live-Modus liefert der Agent
 * neben dem Mittelwert jede Zeile der Tabelle; ohne Live gibt es nur den
 * Mittelwert und damit keine Kerne.
 */
export function kerne(latest: Sample[]): Kern[] {
  const out: Kern[] = []
  for (const s of latest) {
    const index = s.labels?.index
    if (s.metric !== 'cpu_percent' || !index) continue
    out.push({ index, value: s.value })
  }
  return out.sort((a, b) => Number(a.index) - Number(b.index))
}

/** Ein Balken der Speicherkarte. */
export interface SpeicherTeil {
  label: string
  /** Anteil in Prozent, für die Breite des Balkens. */
  anteil: number
  /** Absoluter Wert, lesbar. */
  text: string
  farbe: string
  /** Auffällig, wenn die Auslagerung voll ist. */
  warnung: boolean
}

function wert(latest: Sample[], metric: string): number | undefined {
  return latest.find((s) => s.metric === metric && !s.labels?.index)?.value
}

/**
 * Belegt, Cache/Puffer und Auslagerung mit absoluten Werten.
 *
 * Der Grund für die absoluten Werte ist ein Befund aus dem Betrieb:
 * „Auslagerung 100 %" allein ist nicht überprüfbar. „100 % von 8,0 GB" steht
 * dagegen neben dem, was `free -h` auf dem Host ausgibt.
 */
export function speicherTeile(latest: Sample[]): SpeicherTeil[] {
  const out: SpeicherTeil[] = []
  const memTotal = wert(latest, 'memory_total_bytes')
  const memUsed = wert(latest, 'memory_used_bytes')
  const memCache = wert(latest, 'memory_cache_bytes')
  const swapTotal = wert(latest, 'swap_total_bytes')
  const swapUsed = wert(latest, 'swap_used_bytes')
  const swapPercent = wert(latest, 'swap_percent')

  if (memUsed !== undefined && memTotal) {
    out.push({
      label: 'Belegt',
      anteil: (memUsed / memTotal) * 100,
      text: formatBytes(memUsed),
      farbe: 'var(--info)',
      warnung: false,
    })
  }
  if (memCache !== undefined && memTotal) {
    out.push({
      label: 'Cache/Puffer',
      anteil: (memCache / memTotal) * 100,
      text: formatBytes(memCache),
      farbe: 'var(--muted)',
      warnung: false,
    })
  }
  if (swapUsed !== undefined && swapTotal) {
    const anteil = (swapUsed / swapTotal) * 100
    out.push({
      label: 'Auslagerung',
      anteil,
      text: `${formatBytes(swapUsed)} von ${formatBytes(swapTotal)}`,
      farbe: anteil >= 90 ? 'var(--warn)' : 'var(--ok)',
      warnung: anteil >= 90,
    })
  } else if (swapPercent !== undefined) {
    // Ein älterer Agent meldet nur den Prozentsatz. Dann steht da, was da
    // ist, statt eine Zahl zu erfinden.
    out.push({
      label: 'Auslagerung',
      anteil: swapPercent,
      text: `${formatNumber(swapPercent, '%')} (Agent meldet keine Bytes)`,
      farbe: swapPercent >= 90 ? 'var(--warn)' : 'var(--ok)',
      warnung: swapPercent >= 90,
    })
  }
  return out
}

/** Der jüngste Messpunkt einer Reihe, lesbar. */
function letzterWert(reihe: number[]): string {
  const wert = reihe.at(-1)
  return wert === undefined ? '–' : formatBitsPerSecond(wert)
}

/** Eine Verlaufsabfrage über das kurze Live-Fenster. */
function useLiveVerlauf(deviceId: string, metric: string, port: string, intervalS: number) {
  return useQuery({
    queryKey: ['live-history', deviceId, metric, port],
    queryFn: ({ signal }) =>
      apiGet<Sample[]>(
        `/devices/${deviceId}/history?metrik=${encodeURIComponent(metric)}` +
          (port ? `&port=${encodeURIComponent(port)}` : '') +
          `&zeitraum=${LIVE_FENSTER}`,
        signal,
      ),
    refetchInterval: intervalS * 1000,
  })
}

export function LiveKarten({ deviceId, latest, intervalS, port }: LiveKartenProps) {
  const cpu = useLiveVerlauf(deviceId, 'cpu_percent', '', intervalS)
  const memory = useLiveVerlauf(deviceId, 'memory_percent', '', intervalS)
  const ein = useLiveVerlauf(deviceId, 'if_in_bits_per_s', port, intervalS)
  const aus = useLiveVerlauf(deviceId, 'if_out_bits_per_s', port, intervalS)

  const cpuWerte = werte(cpu.data)
  const memWerte = werte(memory.data)
  const einWerte = werte(ein.data)
  const ausWerte = werte(aus.data)
  const kernListe = kerne(latest)
  const teile = speicherTeile(latest)
  const memPercent = wert(latest, 'memory_percent')
  const memTotal = wert(latest, 'memory_total_bytes')
  const memUsed = wert(latest, 'memory_used_bytes')
  const durchsatzMax = Math.max(...einWerte, ...ausWerte, 1)

  return (
    <div className="live-karten">
      <section className="card live-karte">
        <div className="card-head">
          <h2 className="card-title">CPU gesamt</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            letzte 5 min
          </span>
        </div>
        <div className="card-body">
          <Sparkline
            points={cpuWerte}
            width={400}
            height={110}
            max={100}
            label="CPU-Verlauf der letzten 5 Minuten"
          />
          {kernListe.length > 0 ? (
            <>
              <span className="muted" style={{ fontSize: 12 }}>
                Je Kern ({kernListe.length})
              </span>
              <div className="kern-balken">
                {kernListe.map((k) => (
                  <span
                    key={k.index}
                    className="kern"
                    title={`Kern ${k.index}: ${formatNumber(k.value, '%')}`}
                    style={{
                      height: `${Math.max(3, (k.value / 100) * 34)}px`,
                      background: k.value > 80 ? 'var(--warn)' : 'var(--acc)',
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              Die einzelnen Kerne kommen mit der nächsten Live-Abfrage.
            </span>
          )}
        </div>
      </section>

      <section className="card live-karte">
        <div className="card-head">
          <h2 className="card-title">Arbeitsspeicher</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            {memUsed !== undefined && memTotal
              ? `${formatBytes(memUsed)} von ${formatBytes(memTotal)}`
              : memPercent !== undefined
                ? formatNumber(memPercent, '%')
                : 'letzte 5 min'}
          </span>
        </div>
        <div className="card-body">
          <Sparkline
            points={memWerte}
            width={400}
            height={110}
            max={100}
            color="var(--info)"
            label="Speicher-Verlauf der letzten 5 Minuten"
          />
          <div className="speicher-teile">
            {teile.map((t) => (
              <div className="speicher-teil" key={t.label}>
                <span className="muted">{t.label}</span>
                <span className="balken">
                  <span
                    style={{ width: `${Math.min(100, t.anteil)}%`, background: t.farbe }}
                  />
                </span>
                <span className="mono" style={t.warnung ? { color: 'var(--warn)' } : undefined}>
                  {t.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card live-karte">
        <div className="card-head">
          <h2 className="card-title">Durchsatz {port || '–'}</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            letzte 5 min
          </span>
        </div>
        <div className="card-body">
          {port === '' ? (
            <span className="muted" style={{ fontSize: 13 }}>
              Kein aktiver Port mit Durchsatz.
            </span>
          ) : (
            <>
              <div className="durchsatz-graph">
                <Sparkline
                  points={einWerte}
                  width={400}
                  height={110}
                  max={durchsatzMax}
                  color="var(--ok)"
                  label={`Eingang ${port} der letzten 5 Minuten`}
                />
                <Sparkline
                  points={ausWerte}
                  width={400}
                  height={110}
                  max={durchsatzMax}
                  color="var(--warn)"
                  label={`Ausgang ${port} der letzten 5 Minuten`}
                />
              </div>
              <div className="row" style={{ gap: 18, fontSize: 13 }}>
                <span className="legende">
                  <span className="strich" style={{ background: 'var(--ok)' }} />
                  Eingang{' '}
                  <strong className="mono">{letzterWert(einWerte)}</strong>
                </span>
                <span className="legende">
                  <span className="strich" style={{ background: 'var(--warn)' }} />
                  Ausgang{' '}
                  <strong className="mono">{letzterWert(ausWerte)}</strong>
                </span>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
